import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("convostack_token"));

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem("convostack_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem("convostack_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("convostack_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center thermal-bg">
        <div className="spinner"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  return children;
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-magic-link`, { email });
      setMagicToken(response.data.token);
      setStep("verify");
      toast.success("Magic link sent! Check your email.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send magic link");
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify`, { token: magicToken });
      login(response.data.token, response.data.user);
      toast.success("Welcome to ConvoStack UIM!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired token");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen thermal-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_737002d6-0569-4e9a-8751-23da7764c1f0/artifacts/c3si6q4g_ConvostackMain.png"
            alt="ConvoStack UIM"
            className="w-24 h-24 mx-auto mb-4"
            data-testid="login-logo"
          />
          <h1 className="text-3xl font-bold text-white tracking-tight">ConvoStack UIM</h1>
          <p className="text-sm mt-2 text-gray-400 mono">The stack that thinks back</p>
        </div>

        <div className="thermal-card rounded-xl p-8 glow-orange">
          {step === "email" ? (
            <form onSubmit={handleRequestMagicLink}>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-thermal mb-6"
                required
                data-testid="login-email-input"
              />
              <button type="submit" disabled={loading} className="btn-thermal w-full" data-testid="login-request-btn">
                {loading ? "Sending..." : "Request Magic Link"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <p className="text-sm text-gray-400 mb-4">
                A magic link has been sent to <span className="thermal-orange">{email}</span>
              </p>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">Magic Token</label>
              <input
                type="text"
                value={magicToken}
                onChange={(e) => setMagicToken(e.target.value)}
                placeholder="Paste your magic token"
                className="input-thermal mb-6"
                required
                data-testid="login-token-input"
              />
              <button type="submit" disabled={loading} className="btn-thermal w-full mb-4" data-testid="login-verify-btn">
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
              <button type="button" onClick={() => setStep("email")} className="w-full text-sm text-gray-400 hover:text-white">
                Back to email
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-gray-500 mt-6 mono">Unified Intelligent Middleware v1.1</p>
      </div>
    </div>
  );
};

// Report Detail Page (with polling)
const ReportDetail = () => {
  const { reportId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000  // 300 second timeout
      });
      setReport(response.data);
      return response.data.status;
    } catch (e) {
      toast.error("Failed to load report");
      navigate("/dashboard");
    }
    return null;
  }, [reportId, token, navigate]);

  useEffect(() => {
    let intervalId;
    
    const initFetch = async () => {
      const status = await fetchReport();
      setLoading(false);
      
      // Poll if pending or processing
      if (status === "pending" || status === "processing") {
        intervalId = setInterval(async () => {
          const newStatus = await fetchReport();
          if (newStatus === "completed" || newStatus === "failed") {
            clearInterval(intervalId);
          }
        }, 3000); // Poll every 3 seconds
      }
    };
    
    initFetch();
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [fetchReport]);

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `convostack_report.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const parseFoundation = (text) => {
    if (!text || text === '[]' || text === '') return [];
    try { 
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : [text];
    } catch { 
      return text ? [text] : []; 
    }
  };

  const parseBlueprint = (json) => {
    if (!json) return [];
    const steps = json?.steps || [];
    return Array.isArray(steps) ? steps : [];
  };

  // Truncate query to first 10 words
  const truncateTitle = (query) => {
    if (!query) return '';
    const words = query.split(' ');
    if (words.length <= 10) return query;
    return words.slice(0, 10).join(' ') + '...';
  };

  // Check if content is actually available
  const hasContent = (report) => {
    if (!report) return false;
    if (report.status !== 'completed') return false;
    
    const foundation = parseFoundation(report.foundation_text);
    const surprise = report.surprise_text;
    const blueprint = parseBlueprint(report.blueprint_json);
    
    return (foundation.length > 0 && foundation[0] !== '') || 
           (surprise && surprise.length > 0) || 
           (blueprint.length > 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen thermal-bg flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const isPending = report?.status === "pending" || report?.status === "processing";
  const isFailed = report?.status === "failed";
  const isCompleted = report?.status === "completed";
  const contentReady = hasContent(report);

  return (
    <div className="min-h-screen thermal-bg p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm mb-6" data-testid="breadcrumbs">
          <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">Dashboard</Link>
          <span className="text-gray-600">/</span>
          <span className="text-white truncate max-w-xs">{truncateTitle(report?.query)}</span>
        </nav>

        {/* Back Button */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          data-testid="back-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to My Reports
        </Link>

        {/* Report Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{truncateTitle(report?.query)}</h1>
              <p className="text-xs text-gray-500 mono">
                Created: {new Date(report?.created_at).toLocaleString()}
                {report?.completed_at && ` | Completed: ${new Date(report?.completed_at).toLocaleString()}`}
              </p>
            </div>
            
            {/* Export Buttons (only show when completed with content) */}
            {isCompleted && contentReady && (
              <div className="flex flex-wrap gap-2" data-testid="export-buttons">
                <button onClick={() => handleExport('pdf')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-500 text-white flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" /></svg>
                  PDF
                </button>
                <button onClick={() => handleExport('md')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1">MD</button>
                <button onClick={() => handleExport('txt')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-600 hover:bg-gray-500 text-white flex items-center gap-1">TXT</button>
                <button onClick={() => handleExport('html')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white flex items-center gap-1">HTML</button>
              </div>
            )}
          </div>
        </div>

        {/* Loading/Processing State */}
        {isPending && (
          <div className="thermal-card rounded-xl p-12 text-center" data-testid="loading-state">
            <div className="mb-6">
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full animate-pulse" style={{width: report?.status === "processing" ? "60%" : "20%"}}></div>
              </div>
            </div>
            <div className="spinner mx-auto mb-4"></div>
            <h3 className="text-xl text-white mb-2">
              {report?.status === "processing" ? "Brain is grinding..." : "Initializing analysis..."}
            </h3>
            <p className="text-gray-400 mono text-sm">
              {report?.status === "processing" 
                ? "Vec, Cal, Bol, and Naz are debating your query"
                : "Preparing the ConvoStack agents"}
            </p>
            <p className="text-gray-500 text-xs mt-4 mono">This may take 60-90 seconds</p>
          </div>
        )}

        {/* Failed State */}
        {isFailed && (
          <div className="thermal-card rounded-xl p-12 text-center border-red-600/50">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-600/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-xl text-white mb-2">Generation Failed</h3>
            <p className="text-gray-400 text-sm mb-4">{report?.error_message || "An unexpected error occurred"}</p>
            <Link to="/dashboard" className="btn-thermal inline-block">Try Again</Link>
          </div>
        )}

        {/* Completed but content still loading */}
        {isCompleted && !contentReady && (
          <div className="thermal-card rounded-xl p-12 text-center" data-testid="content-loading">
            <div className="spinner mx-auto mb-4"></div>
            <h3 className="text-xl text-white mb-2">Loading report content...</h3>
            <p className="text-gray-400 mono text-sm">Content is being retrieved</p>
          </div>
        )}

        {/* Completed Report with Content */}
        {isCompleted && contentReady && (
          <div className="space-y-6 animate-fade-in" data-testid="report-content">
            {/* Foundation */}
            <div className="report-section">
              <h3 className="report-section-title">I. The Foundation (Verified Trends)</h3>
              <ul className="space-y-3">
                {parseFoundation(report.foundation_text).map((item, index) => (
                  <li key={index} className="flex gap-3" style={{ fontSize: '16px', lineHeight: '1.7' }}>
                    <span className="thermal-orange font-bold flex-shrink-0">•</span>
                    <span className="text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Shocking Surprise */}
            <div className="report-section shocking-surprise">
              <h3 className="report-section-title">II. The ConvoStack "Shocking Surprise"</h3>
              <p className="text-gray-200" style={{ fontSize: '16px', lineHeight: '1.8' }}>{report.surprise_text}</p>
            </div>

            {/* Strategic Blueprint */}
            <div className="report-section">
              <h3 className="report-section-title">III. The Strategic Blueprint</h3>
              <ol className="space-y-6">
                {parseBlueprint(report.blueprint_json).map((step, index) => (
                  <li key={index} className="flex gap-4">
                    <span className="thermal-orange font-bold mono flex-shrink-0 w-8">{index + 1}.</span>
                    <p className="text-gray-200" style={{ fontSize: '16px', lineHeight: '1.8' }}>{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Pivot Choice */}
            {report.pivot_choice && Object.keys(report.pivot_choice).length > 0 && (
              <div className="report-section">
                <h3 className="report-section-title">IV. The Pivot Choice</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-5 rounded-lg bg-gray-800 border border-gray-700">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center text-sm">A</span>
                      {report.pivot_choice.path_a?.name || "The Stability Route"}
                    </h4>
                    <p className="text-gray-300" style={{ fontSize: '16px', lineHeight: '1.7' }}>{report.pivot_choice.path_a?.description}</p>
                  </div>
                  <div className="p-5 rounded-lg bg-gray-800 border border-orange-700/50">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full thermal-orange-bg flex items-center justify-center text-sm text-white">B</span>
                      {report.pivot_choice.path_b?.name || "The Aggressive Growth Route"}
                    </h4>
                    <p className="text-gray-300" style={{ fontSize: '16px', lineHeight: '1.7' }}>{report.pivot_choice.path_b?.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const [query, setQuery] = useState("");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(response.data);
    } catch (e) {
      console.error("Failed to fetch reports", e);
    }
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/reports/generate`,
        { query },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Report generation started!");
      setQuery("");
      // Navigate to report detail page immediately
      navigate(`/report/${response.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start report generation");
    }
    setLoading(false);
  };

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports((prev) => prev.filter((r) => r.id !== reportId));
      toast.success("Report deleted");
    } catch (e) {
      toast.error("Failed to delete report");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-yellow-600/20 text-yellow-400",
      processing: "bg-blue-600/20 text-blue-400",
      completed: "bg-green-600/20 text-green-400",
      failed: "bg-red-600/20 text-red-400"
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="min-h-screen thermal-bg flex">
      {/* Sidebar */}
      <div className={`sidebar w-72 flex-shrink-0 ${sidebarOpen ? "" : "hidden"}`}>
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_737002d6-0569-4e9a-8751-23da7764c1f0/artifacts/c3si6q4g_ConvostackMain.png" alt="Logo" className="w-10 h-10" />
            <div>
              <h2 className="font-semibold text-white">ConvoStack</h2>
              <p className="text-xs text-gray-400 mono">UIM</p>
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-gray-700">
          <p className="text-xs text-gray-400 mono uppercase tracking-wider mb-1">Logged in as</p>
          <p className="text-sm text-white truncate">{user?.email}</p>
          {user?.is_admin && (
            <span className="inline-block mt-2 px-2 py-1 text-xs rounded thermal-amber-bg text-black font-medium">Admin</span>
          )}
        </div>

        <div className="p-4">
          <p className="text-xs text-gray-400 mono uppercase tracking-wider mb-3">Report History</p>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {reports.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No reports yet</p>
            ) : (
              reports.map((report) => (
                <div
                  key={report.id}
                  className="sidebar-item group cursor-pointer"
                  onClick={() => navigate(`/report/${report.id}`)}
                  data-testid={`report-item-${report.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{report.query}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusBadge(report.status)}`}>
                          {report.status}
                        </span>
                        <span className="text-xs text-gray-500 mono">
                          {new Date(report.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDeleteReport(report.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 transition-opacity p-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Admin Link */}
        {user?.is_admin && (
          <div className="p-4 border-t border-gray-700">
            <Link to="/admin" className="block text-sm text-amber-400 hover:text-amber-300 transition-colors">
              Admin Dashboard →
            </Link>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          <button onClick={logout} className="w-full text-sm text-gray-400 hover:text-white py-2" data-testid="logout-btn">
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 lg:p-8 overflow-auto">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden fixed top-4 left-4 z-50 p-2 thermal-card rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="max-w-3xl mx-auto mb-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">General Research Box</h1>
            <p className="text-gray-400 text-sm mono">Transform raw data into strategic intelligence</p>
          </div>

          <form onSubmit={handleGenerateReport} className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter your research query..."
              className="input-thermal pr-32 text-lg py-4"
              disabled={loading}
              data-testid="research-input"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 btn-thermal py-2 px-4"
              data-testid="generate-report-btn"
            >
              {loading ? "Starting..." : "Analyze"}
            </button>
          </form>
        </div>

        <div className="max-w-3xl mx-auto text-center py-16">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full thermal-card flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg text-gray-400 mb-2">Ready for Analysis</h3>
          <p className="text-sm text-gray-500 mono">Enter a query above to generate your strategic report</p>
        </div>
      </div>
    </div>
  );
};

// Admin Page with Agent Settings
const AdminPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [agentSettings, setAgentSettings] = useState(null);
  const [activeTab, setActiveTab] = useState("stats");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.is_admin) fetchAdminData();
  }, [user]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, reportsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/agent-settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setReports(reportsRes.data);
      setAgentSettings(settingsRes.data);
    } catch (e) {
      console.error("Failed to fetch admin data", e);
    }
  };

  const handleSaveAgentSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/agent-settings`, agentSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent settings saved!");
    } catch (e) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const handleResetSettings = async () => {
    try {
      const response = await axios.post(`${API}/admin/agent-settings/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentSettings(response.data);
      toast.success("Settings reset to defaults");
    } catch (e) {
      toast.error("Failed to reset settings");
    }
  };

  const updateAgentSetting = (agent, field, value) => {
    setAgentSettings(prev => ({
      ...prev,
      [agent]: { ...prev[agent], [field]: value }
    }));
  };

  if (!user?.is_admin) return <Navigate to="/dashboard" />;

  return (
    <div className="min-h-screen thermal-bg p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">← Back to Dashboard</Link>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {["stats", "users", "reports", "agents"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg mono text-sm transition-colors ${
                activeTab === tab ? "thermal-orange-bg text-white" : "thermal-card text-gray-400 hover:text-white"
              }`}
            >
              {tab === "agents" ? "Agent Settings" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === "stats" && stats && (
          <div className="grid md:grid-cols-4 gap-6">
            <div className="thermal-card rounded-xl p-6">
              <p className="text-xs text-gray-400 mono uppercase mb-2">Total Users</p>
              <p className="text-4xl font-bold thermal-orange">{stats.total_users}</p>
            </div>
            <div className="thermal-card rounded-xl p-6">
              <p className="text-xs text-gray-400 mono uppercase mb-2">Total Reports</p>
              <p className="text-4xl font-bold thermal-amber">{stats.total_reports}</p>
            </div>
            <div className="thermal-card rounded-xl p-6">
              <p className="text-xs text-gray-400 mono uppercase mb-2">Pending</p>
              <p className="text-4xl font-bold text-yellow-400">{stats.pending_reports}</p>
            </div>
            <div className="thermal-card rounded-xl p-6">
              <p className="text-xs text-gray-400 mono uppercase mb-2">Processing</p>
              <p className="text-4xl font-bold text-blue-400">{stats.processing_reports}</p>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="thermal-card rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="text-left p-4 text-xs uppercase mono text-gray-400">Email</th>
                  <th className="text-left p-4 text-xs uppercase mono text-gray-400">Subdomain</th>
                  <th className="text-left p-4 text-xs uppercase mono text-gray-400">Admin</th>
                  <th className="text-left p-4 text-xs uppercase mono text-gray-400">Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-700">
                    <td className="p-4 text-white">{u.email}</td>
                    <td className="p-4 text-gray-400 mono text-sm">{u.subdomain_prefix}</td>
                    <td className="p-4">{u.is_admin ? <span className="px-2 py-1 text-xs rounded thermal-amber-bg text-black">Yes</span> : <span className="text-gray-500">No</span>}</td>
                    <td className="p-4 text-gray-400 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="space-y-4">
            {reports.slice(0, 20).map((report) => (
              <div key={report.id} className="thermal-card rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{report.query}</p>
                    <p className="text-xs text-gray-500 mono mt-1">
                      Status: <span className={`${report.status === 'completed' ? 'text-green-400' : report.status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>{report.status}</span>
                      {" | "}User: {report.user_id.slice(0, 8)}... | {new Date(report.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Agent Settings Tab */}
        {activeTab === "agents" && agentSettings && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Agent System Instructions</h2>
              <div className="flex gap-2">
                <button onClick={handleResetSettings} className="px-4 py-2 text-sm rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600">
                  Reset to Defaults
                </button>
                <button onClick={handleSaveAgentSettings} disabled={saving} className="btn-thermal px-4 py-2 text-sm">
                  {saving ? "Saving..." : "Save Settings"}
                </button>
              </div>
            </div>

            {/* Vec Settings */}
            <div className="thermal-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{agentSettings.vec?.name || "Vec (The Scout)"}</h3>
              <p className="text-sm text-gray-400 mb-4">{agentSettings.vec?.description}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">Search Depth (Top N Results)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={agentSettings.vec?.search_depth || 3}
                    onChange={(e) => updateAgentSetting('vec', 'search_depth', parseInt(e.target.value))}
                    className="input-thermal w-24"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">System Prompt</label>
                  <textarea
                    value={agentSettings.vec?.system_prompt || ''}
                    onChange={(e) => updateAgentSetting('vec', 'system_prompt', e.target.value)}
                    className="input-thermal w-full h-24 resize-none"
                    placeholder="Instructions for Vec..."
                  />
                </div>
              </div>
            </div>

            {/* Cal Settings */}
            <div className="thermal-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{agentSettings.cal?.name || "Cal (The Analyst)"}</h3>
              <p className="text-sm text-gray-400 mb-4">{agentSettings.cal?.description}</p>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">System Prompt</label>
                <textarea
                  value={agentSettings.cal?.system_prompt || ''}
                  onChange={(e) => updateAgentSetting('cal', 'system_prompt', e.target.value)}
                  className="input-thermal w-full h-32 resize-none"
                  placeholder="Instructions for Cal..."
                />
              </div>
            </div>

            {/* Bol Settings */}
            <div className="thermal-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{agentSettings.bol?.name || "Bol (The Provocateur)"}</h3>
              <p className="text-sm text-gray-400 mb-4">{agentSettings.bol?.description}</p>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">System Prompt</label>
                <textarea
                  value={agentSettings.bol?.system_prompt || ''}
                  onChange={(e) => updateAgentSetting('bol', 'system_prompt', e.target.value)}
                  className="input-thermal w-full h-32 resize-none"
                  placeholder="Instructions for Bol..."
                />
              </div>
            </div>

            {/* Naz Settings */}
            <div className="thermal-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{agentSettings.naz?.name || "Naz (The Architect)"}</h3>
              <p className="text-sm text-gray-400 mb-4">{agentSettings.naz?.description}</p>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 mono">System Prompt</label>
                <textarea
                  value={agentSettings.naz?.system_prompt || ''}
                  onChange={(e) => updateAgentSetting('naz', 'system_prompt', e.target.value)}
                  className="input-thermal w-full h-32 resize-none"
                  placeholder="Instructions for Naz..."
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={{ style: { background: "#2D2D2E", color: "#E8E8E8", border: "1px solid #3D3D3E" } }} />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/report/:reportId" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
